import { LearningMap, Achievement } from '../types/learning';

// Ancient Aliens Learning Map Data
export const ancientAliensLearningMap: LearningMap = {
  id: 'ancient-aliens-map',
  title: 'Ancient Aliens: A Comprehensive Learning Journey',
  description: 'Explore the fascinating world of ancient alien theories, archaeological evidence, and alternative historical perspectives.',
  domain: 'ancient_history',
  isPublic: true,
  createdBy: 'system',
  createdAt: '2024-01-01T00:00:00Z',
  metadata: {
    difficulty: 'intermediate',
    estimatedTotalTime: 480, // 8 hours
    targetAudience: 'history enthusiasts, archaeology students, curious minds'
  },
  nodes: [
    // Core Foundation Nodes
    {
      id: 'foundations',
      type: 'topic',
      title: 'Foundations of Ancient Alien Theory',
      description: 'Understanding the basic principles and key figures in ancient alien research',
      difficultyLevel: 3,
      estimatedTimeMinutes: 60,
      prerequisites: [],
      learningObjectives: [
        'Understand the core concepts of ancient alien theory',
        'Identify key researchers and their contributions',
        'Recognize the difference between evidence and speculation'
      ],
      position: { x: 0, y: 0 },
      learningStyleTags: { reading: 0.8, visual: 0.7 },
      metadata: { color: '#4CAF50', importance: 'high' }
    },
    {
      id: 'erich-von-daniken',
      type: 'skill',
      title: 'Erich von Däniken & Chariots of the Gods',
      description: 'The father of ancient alien theory and his groundbreaking work',
      difficultyLevel: 4,
      estimatedTimeMinutes: 45,
      prerequisites: ['foundations'],
      learningObjectives: [
        'Analyze von Däniken\'s key arguments',
        'Evaluate the evidence presented in Chariots of the Gods',
        'Understand the historical context of his work'
      ],
      position: { x: -200, y: -100 },
      learningStyleTags: { reading: 0.9, visual: 0.6 },
      metadata: { color: '#2196F3', importance: 'high' }
    },
    {
      id: 'archaeological-evidence',
      type: 'topic',
      title: 'Archaeological Evidence',
      description: 'Physical artifacts and structures that suggest advanced ancient technology',
      difficultyLevel: 5,
      estimatedTimeMinutes: 90,
      prerequisites: ['foundations'],
      learningObjectives: [
        'Examine controversial archaeological sites',
        'Analyze construction techniques of ancient monuments',
        'Evaluate alternative explanations for ancient technology'
      ],
      position: { x: 200, y: -100 },
      learningStyleTags: { visual: 0.9, kinesthetic: 0.7 },
      metadata: { color: '#FF9800', importance: 'high' }
    },
    {
      id: 'pyramids-giza',
      type: 'skill',
      title: 'Great Pyramids of Giza',
      description: 'The most famous ancient structures and their mysterious construction',
      difficultyLevel: 6,
      estimatedTimeMinutes: 75,
      prerequisites: ['archaeological-evidence'],
      learningObjectives: [
        'Understand pyramid construction theories',
        'Analyze precision and alignment of the pyramids',
        'Evaluate mainstream vs alternative explanations'
      ],
      position: { x: 100, y: -200 },
      learningStyleTags: { visual: 0.9, reading: 0.7 },
      metadata: { color: '#9C27B0', importance: 'high' }
    },
    {
      id: 'puma-punku',
      type: 'skill',
      title: 'Puma Punku & Tiwanaku',
      description: 'Inca sites with incredibly precise stone cutting technology',
      difficultyLevel: 7,
      estimatedTimeMinutes: 60,
      prerequisites: ['archaeological-evidence'],
      learningObjectives: [
        'Examine the precision of stone cutting at Puma Punku',
        'Understand the geological challenges of the site',
        'Compare with modern stone cutting capabilities'
      ],
      position: { x: 300, y: -200 },
      learningStyleTags: { visual: 0.9, kinesthetic: 0.8 },
      metadata: { color: '#9C27B0', importance: 'medium' }
    },
    {
      id: 'ancient-texts',
      type: 'topic',
      title: 'Ancient Texts & Descriptions',
      description: 'Written accounts that describe advanced technology or beings',
      difficultyLevel: 4,
      estimatedTimeMinutes: 80,
      prerequisites: ['foundations'],
      learningObjectives: [
        'Analyze ancient texts for technological descriptions',
        'Understand cultural context of ancient writings',
        'Evaluate translation and interpretation issues'
      ],
      position: { x: -200, y: 100 },
      learningStyleTags: { reading: 0.9, verbal: 0.8 },
      metadata: { color: '#FF5722', importance: 'high' }
    },
    {
      id: 'sumerian-texts',
      type: 'skill',
      title: 'Sumerian Cuneiform Texts',
      description: 'The oldest known written language and its descriptions of gods',
      difficultyLevel: 5,
      estimatedTimeMinutes: 70,
      prerequisites: ['ancient-texts'],
      learningObjectives: [
        'Read and interpret Sumerian cuneiform',
        'Analyze descriptions of the Anunnaki',
        'Understand the cultural significance of these texts'
      ],
      position: { x: -300, y: 200 },
      learningStyleTags: { reading: 0.9, verbal: 0.9 },
      metadata: { color: '#795548', importance: 'high' }
    },
    {
      id: 'vedic-texts',
      type: 'skill',
      title: 'Vedic Sanskrit Texts',
      description: 'Ancient Indian texts describing advanced technology and flying machines',
      difficultyLevel: 6,
      estimatedTimeMinutes: 65,
      prerequisites: ['ancient-texts'],
      learningObjectives: [
        'Analyze descriptions of vimanas (flying machines)',
        'Understand the technological concepts in Vedic texts',
        'Compare with modern aerospace technology'
      ],
      position: { x: -100, y: 200 },
      learningStyleTags: { reading: 0.9, visual: 0.7 },
      metadata: { color: '#795548', importance: 'medium' }
    },
    {
      id: 'crop-circles',
      type: 'topic',
      title: 'Crop Circles & Geometric Patterns',
      description: 'Mysterious patterns in fields and their possible ancient connections',
      difficultyLevel: 3,
      estimatedTimeMinutes: 50,
      prerequisites: ['foundations'],
      learningObjectives: [
        'Understand the history of crop circles',
        'Analyze geometric patterns and their significance',
        'Evaluate hoax vs genuine phenomena'
      ],
      position: { x: 0, y: 200 },
      learningStyleTags: { visual: 0.9, kinesthetic: 0.6 },
      metadata: { color: '#4CAF50', importance: 'low' }
    },
    {
      id: 'scientific-criticism',
      type: 'topic',
      title: 'Scientific Criticism & Skepticism',
      description: 'Mainstream scientific responses to ancient alien theories',
      difficultyLevel: 6,
      estimatedTimeMinutes: 70,
      prerequisites: ['foundations', 'archaeological-evidence'],
      learningObjectives: [
        'Understand scientific methodology',
        'Analyze counter-arguments to ancient alien theories',
        'Develop critical thinking skills'
      ],
      position: { x: 200, y: 100 },
      learningStyleTags: { reading: 0.9, sequential: 0.8 },
      metadata: { color: '#607D8B', importance: 'high' }
    },
    {
      id: 'modern-research',
      type: 'topic',
      title: 'Modern Research & Technology',
      description: 'Contemporary investigations using advanced technology',
      difficultyLevel: 7,
      estimatedTimeMinutes: 85,
      prerequisites: ['scientific-criticism'],
      learningObjectives: [
        'Understand modern archaeological techniques',
        'Analyze recent discoveries and their implications',
        'Evaluate the role of technology in archaeology'
      ],
      position: { x: 400, y: 100 },
      learningStyleTags: { visual: 0.8, reading: 0.8 },
      metadata: { color: '#3F51B5', importance: 'medium' }
    }
  ],
  resources: [
    {
      id: 'chariots-of-gods-book',
      nodeId: 'erich-von-daniken',
      type: 'article',
      title: 'Chariots of the Gods - Key Excerpts',
      description: 'Essential passages from von Däniken\'s groundbreaking work',
      content: 'In his 1968 book "Chariots of the Gods," Erich von Däniken proposed that ancient civilizations were visited by extraterrestrial beings who shared advanced technology and knowledge...',
      difficultyLevel: 4,
      estimatedTimeMinutes: 30,
      learningStyleTags: { reading: 0.9, visual: 0.6 },
      prerequisites: [],
      metadata: { source: 'book', pages: '1-50' }
    },
    {
      id: 'pyramid-construction-video',
      nodeId: 'pyramids-giza',
      type: 'video',
      title: 'Pyramid Construction Theories Explained',
      description: 'Visual analysis of different pyramid construction theories',
      url: 'https://example.com/pyramid-construction',
      difficultyLevel: 6,
      estimatedTimeMinutes: 45,
      learningStyleTags: { visual: 0.9, auditory: 0.8 },
      prerequisites: ['archaeological-evidence'],
      metadata: { duration: '45:00', quality: 'HD' }
    },
    {
      id: 'puma-punku-interactive',
      nodeId: 'puma-punku',
      type: 'interactive',
      title: 'Puma Punku Stone Analysis Tool',
      description: 'Interactive 3D model to examine stone cutting precision',
      content: 'Explore the incredible precision of the stone blocks at Puma Punku using our interactive 3D model...',
      difficultyLevel: 7,
      estimatedTimeMinutes: 60,
      learningStyleTags: { kinesthetic: 0.9, visual: 0.9 },
      prerequisites: ['archaeological-evidence'],
      metadata: { type: '3d-model', interactive: true }
    },
    {
      id: 'sumerian-translation-exercise',
      nodeId: 'sumerian-texts',
      type: 'exercise',
      title: 'Sumerian Cuneiform Translation Practice',
      description: 'Hands-on exercise to learn basic cuneiform translation',
      content: 'Practice translating simple Sumerian cuneiform texts and identify references to advanced technology...',
      difficultyLevel: 5,
      estimatedTimeMinutes: 40,
      learningStyleTags: { kinesthetic: 0.9, reading: 0.8 },
      prerequisites: ['ancient-texts'],
      metadata: { type: 'translation', difficulty: 'beginner' }
    },
    {
      id: 'crop-circles-analysis',
      nodeId: 'crop-circles',
      type: 'article',
      title: 'Crop Circle Pattern Analysis',
      description: 'Mathematical analysis of crop circle geometries',
      content: 'Examine the mathematical precision and geometric patterns found in crop circles...',
      difficultyLevel: 3,
      estimatedTimeMinutes: 25,
      learningStyleTags: { visual: 0.9, reading: 0.7 },
      prerequisites: [],
      metadata: { type: 'mathematical', complexity: 'basic' }
    }
  ],
  assessments: [
    {
      id: 'learning-style-assessment',
      nodeId: 'foundations',
      questionType: 'learning_preference',
      questionText: 'How do you prefer to learn about ancient civilizations?',
      options: [
        'Reading detailed articles and books',
        'Watching documentaries and videos',
        'Hands-on activities and interactive tools',
        'Listening to lectures and discussions'
      ],
      learningStyleWeight: { reading: 0.3, visual: 0.3, kinesthetic: 0.3, auditory: 0.3 },
      difficultyLevel: 1,
      metadata: { category: 'learning_style' }
    },
    {
      id: 'pyramid-knowledge-check',
      nodeId: 'pyramids-giza',
      questionType: 'multiple_choice',
      questionText: 'What is the most controversial aspect of the Great Pyramid\'s construction?',
      options: [
        'The precision of the stone cutting',
        'The alignment with celestial bodies',
        'The weight of the individual blocks',
        'The height of the structure'
      ],
      correctAnswer: 'The precision of the stone cutting',
      learningStyleWeight: { reading: 0.8, visual: 0.6 },
      difficultyLevel: 6,
      metadata: { category: 'knowledge_check' }
    },
    {
      id: 'puma-punku-confidence',
      nodeId: 'puma-punku',
      questionType: 'self_reflection',
      questionText: 'How confident are you in explaining the construction techniques used at Puma Punku?',
      options: ['Not confident at all', 'Slightly confident', 'Moderately confident', 'Very confident', 'Extremely confident'],
      learningStyleWeight: { kinesthetic: 0.7, visual: 0.6 },
      difficultyLevel: 7,
      metadata: { category: 'self_assessment' }
    },
    {
      id: 'ancient-texts-scenario',
      nodeId: 'ancient-texts',
      questionType: 'scenario',
      questionText: 'You discover an ancient text that describes "flying chariots of fire." How would you approach analyzing this text?',
      options: [
        'Compare it with other ancient texts from the same period',
        'Look for similar descriptions in different cultures',
        'Analyze the linguistic context and possible translations',
        'All of the above'
      ],
      correctAnswer: 'All of the above',
      learningStyleWeight: { reading: 0.9, verbal: 0.8 },
      difficultyLevel: 5,
      metadata: { category: 'critical_thinking' }
    },
    {
      id: 'scientific-method-understanding',
      nodeId: 'scientific-criticism',
      questionType: 'likert_scale',
      questionText: 'Rate your understanding of how scientific methodology applies to ancient alien theories:',
      options: ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree'],
      learningStyleWeight: { reading: 0.8, sequential: 0.9 },
      difficultyLevel: 6,
      metadata: { category: 'understanding_assessment' }
    }
  ]
};

export const learningAchievements: Achievement[] = [
  {
    id: 'first-steps',
    title: 'First Steps',
    description: 'Complete your first learning node',
    icon: '👣',
    criteria: { type: 'nodes_completed', threshold: 1 },
    rarity: 'common',
    points: 10
  },
  {
    id: 'pyramid-master',
    title: 'Pyramid Master',
    description: 'Achieve 90% mastery in pyramid construction knowledge',
    icon: '🔺',
    criteria: { type: 'mastery_level', threshold: 0.9, nodeIds: ['pyramids-giza'] },
    rarity: 'uncommon',
    points: 50
  },
  {
    id: 'ancient-scholar',
    title: 'Ancient Scholar',
    description: 'Complete all ancient texts learning modules',
    icon: '📜',
    criteria: { type: 'nodes_completed', threshold: 3, nodeIds: ['ancient-texts', 'sumerian-texts', 'vedic-texts'] },
    rarity: 'rare',
    points: 100
  },
  {
    id: 'critical-thinker',
    title: 'Critical Thinker',
    description: 'Complete the scientific criticism module with high scores',
    icon: '🧠',
    criteria: { type: 'quiz_score', threshold: 0.85, nodeIds: ['scientific-criticism'] },
    rarity: 'epic',
    points: 200
  },
  {
    id: 'ancient-aliens-expert',
    title: 'Ancient Aliens Expert',
    description: 'Complete the entire learning path with 80% average mastery',
    icon: '👽',
    criteria: { type: 'mastery_level', threshold: 0.8 },
    rarity: 'legendary',
    points: 500
  }
];

export const sampleLearningStyleProfile = {
  visual: 0.8,
  auditory: 0.6,
  reading: 0.9,
  kinesthetic: 0.7,
  sequential: 0.6,
  active: 0.8,
  sensing: 0.7,
  verbal: 0.8
};

export const sampleLearningPath = {
  id: 'sample-path-1',
  userId: 'user-123',
  mapId: 'ancient-aliens-map',
  currentNodeId: 'foundations',
  progress: {
    'foundations': 0.3,
    'erich-von-daniken': 0.0,
    'archaeological-evidence': 0.0,
    'pyramids-giza': 0.0,
    'puma-punku': 0.0,
    'ancient-texts': 0.0,
    'sumerian-texts': 0.0,
    'vedic-texts': 0.0,
    'crop-circles': 0.0,
    'scientific-criticism': 0.0,
    'modern-research': 0.0
  },
  learningStyleProfile: sampleLearningStyleProfile,
  preferences: {
    preferredSessionLength: 45,
    preferredDifficulty: 5,
    preferredResourceTypes: ['article', 'interactive', 'video'],
    timeOfDay: 'evening' as 'morning' | 'afternoon' | 'evening'
  },
  achievements: ['first-steps'],
  totalTimeSpent: 25,
  createdAt: '2024-01-01T00:00:00Z',
  lastAccessed: '2024-01-15T14:30:00Z'
};
